import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { fetchAllUsers, updateUserAdmin, deleteUser } from '../services/userService';
import { fetchAllGroups, fetchGroupsWithMembers, updateUserGroups } from '../services/groupService';
import ActionDropdown from './ActionDropdown';
import ConfirmDialog from './ConfirmDialog';
import EditGroupsModal from './EditGroupsModal';

const UserManagementTable = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsWithMembers, setGroupsWithMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [editGroupsModal, setEditGroupsModal] = useState({ isOpen: false });

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, groupsData, groupsWithMembersData] = await Promise.all([
        fetchAllUsers(),
        fetchAllGroups(),
        fetchGroupsWithMembers(),
      ]);

      setUsers(usersData);
      setGroups(groupsData);
      setGroupsWithMembers(groupsWithMembersData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getUserDisplayName = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user.email;
  };

  const getUserGroups = (userId) => {
    const userGroups = groupsWithMembers
      .filter(group => group.GroupMember.some(member => member.user.id === userId))
      .map(group => group.name);
    return userGroups;
  };

  const getUserGroupIds = (userId) => {
    const userGroupIds = groupsWithMembers
      .filter(group => group.GroupMember.some(member => member.user.id === userId))
      .map(group => group.id);
    return userGroupIds;
  };

  const handleToggleAdmin = async (user) => {
    const newAdminStatus = !user.is_admin;

    // Optimistic update
    const originalUsers = [...users];
    setUsers(users.map(u => u.id === user.id ? { ...u, is_admin: newAdminStatus } : u));

    try {
      await updateUserAdmin(user.id, newAdminStatus);
      await loadData(); // Refresh to ensure consistency
    } catch (err) {
      console.error('Error updating admin status:', err);
      setUsers(originalUsers); // Revert on error
      alert('Failed to update admin status. Please try again.');
    }
  };

  const handleDeleteUser = async (userId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false });

        // Optimistic update
        const originalUsers = [...users];
        setUsers(users.filter(u => u.id !== userId));

        try {
          await deleteUser(userId);
          await loadData(); // Refresh to ensure consistency
        } catch (err) {
          console.error('Error deleting user:', err);
          setUsers(originalUsers); // Revert on error
          alert('Failed to delete user. Please try again.');
        }
      },
      onCancel: () => {
        setConfirmDialog({ isOpen: false });
      },
    });
  };

  const handleEditGroups = (user) => {
    setEditGroupsModal({
      isOpen: true,
      user: {
        id: user.id,
        displayName: getUserDisplayName(user),
        groupIds: getUserGroupIds(user.id),
      },
    });
  };

  const handleSaveGroups = async (userId, groupIds) => {
    setEditGroupsModal({ isOpen: false });

    try {
      await updateUserGroups(userId, groupIds);
      await loadData(); // Refresh to show updated groups
    } catch (err) {
      console.error('Error updating user groups:', err);
      alert('Failed to update user groups. Please try again.');
    }
  };

  const getActions = (user) => [
    {
      label: 'Change Groups',
      onClick: () => handleEditGroups(user),
    },
    {
      label: user.is_admin ? 'Remove Admin' : 'Set as Admin',
      onClick: () => handleToggleAdmin(user),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-red-800 font-semibold mb-2">Error loading users</h3>
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-6 font-medium text-gray-900">Full Name</th>
                <th className="text-left py-3 px-6 font-medium text-gray-900">Groups</th>
                <th className="text-left py-3 px-6 font-medium text-gray-900">Admin Status</th>
                <th className="text-right py-3 px-6 font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No users found</p>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                          {getUserDisplayName(user)[0]}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {getUserDisplayName(user)}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-wrap gap-2">
                        {getUserGroups(user.id).length === 0 ? (
                          <span className="text-gray-400 text-sm">No groups</span>
                        ) : (
                          getUserGroups(user.id).map((groupName) => (
                            <span
                              key={groupName}
                              className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                            >
                              {groupName}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {user.is_admin ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                          Admin
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                          User
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end">
                        <ActionDropdown actions={getActions(user)} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />

      <EditGroupsModal
        isOpen={editGroupsModal.isOpen}
        user={editGroupsModal.user}
        allGroups={groups}
        onSave={handleSaveGroups}
        onCancel={() => setEditGroupsModal({ isOpen: false })}
      />
    </>
  );
};

export default UserManagementTable;
